import type { MessageTemplate } from "@repo/app-types/entities/messageTemplate";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { IconBellOff, IconCheck, IconPlus } from "@tabler/icons-react";
import { type FC, type ReactNode, useMemo } from "react";
import {
  Button as AriaButton,
  type Key,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
} from "react-aria-components";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { appPaths } from "../../app/appPaths.js";
import { MessageTemplateCard } from "./messageTemplateCard.js";

export interface MessageTemplateFieldProps {
  label: ReactNode;
  template: MessageTemplate | undefined;
  templates: MessageTemplate[];
  onChange: (template: MessageTemplate | undefined) => void;
  isDisabled?: boolean;
}

type MessageTemplateItem =
  | NewMessageTemplateItem
  | ExistingMessageTemplateItem
  | NoMessageTemplateItem;

interface NewMessageTemplateItem {
  id: "new";
  type: "new";
}

interface ExistingMessageTemplateItem {
  type: "existing";
  id: Key;
  template: MessageTemplate;
}

interface NoMessageTemplateItem {
  id: "none";
  type: "none";
}

export const MessageTemplateField: FC<MessageTemplateFieldProps> = ({
  label,
  template,
  templates,
  onChange,
  isDisabled,
}) => {
  const navigate = useNavigate();

  const items: readonly MessageTemplateItem[] = useMemo(
    () => [
      { type: "new" as const, id: "new" },
      { type: "none" as const, id: "none" },
      ...templates.map((template) => ({
        type: "existing" as const,
        id: template.id,
        template,
      })),
    ],
    [templates],
  );

  return (
    <div className="relative">
      <Select
        className="flex flex-col space-y-2"
        selectedKey={template?.id ?? "none"}
        onSelectionChange={(key) => {
          if (key === "new") {
            navigate(appPaths.settingsMessageTemplates.build());
          } else if (key === "none") {
            onChange(undefined);
          } else {
            onChange(templates.find((t) => t.id === key));
          }
        }}
        isDisabled={isDisabled}
      >
        <Label size={Size.SMALL}>{label}</Label>
        <AriaButton
          className={twMerge(
            "flex w-max flex-row items-center justify-between space-x-2 overflow-hidden rounded-lg bg-white py-2 pr-3 pl-3 text-left text-foreground",
            template
              ? "border border-border-strong shadow-xs"
              : "border border-dashed border-border-strong hover:bg-gray-100",
          )}
        >
          <MessageTemplateCard template={template} />
        </AriaButton>
        <Popover className="overflow-auto rounded-md bg-white py-1.5 shadow-lg ring-1 ring-black/5 data-[entering]:animate-popover-enter data-[exiting]:animate-popover-exit">
          <ListBox items={items}>
            {(item) => (
              <ListBoxItem
                className="flex cursor-pointer flex-row items-center justify-between space-x-2 py-2 pr-2 pl-3 focus:bg-gray-100 focus:outline-hidden"
                textValue="Message template"
              >
                {({ isSelected }) => (
                  <>
                    {item.type === "new" ? (
                      <div className="flex max-w-72 flex-row items-center space-x-3 overflow-hidden text-foreground">
                        <IconPlus className="size-4 shrink-0" />
                        <div className="text-sm">
                          Create a new message template
                        </div>
                      </div>
                    ) : item.type === "none" ? (
                      <div className="flex max-w-72 flex-row items-center space-x-3 overflow-hidden text-foreground">
                        <IconBellOff className="size-4 shrink-0" />
                        <div className="text-sm">No message</div>
                      </div>
                    ) : (
                      <MessageTemplateCard template={item.template} />
                    )}
                    <IconCheck
                      aria-hidden="true"
                      className={twMerge(
                        "size-5 shrink-0 text-indigo-600",
                        isSelected ? "visible" : "invisible",
                      )}
                    />
                  </>
                )}
              </ListBoxItem>
            )}
          </ListBox>
        </Popover>
      </Select>
    </div>
  );
};
